/**
 * Agent Testing Page
 *
 * Deliberately separates formal evaluation evidence from ad-hoc sandbox
 * experiments so operators know which workflow they are in.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Layout from '../../components/layout/Layout'
import {
  Page,
  PageBody,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelDescription,
  PanelBody,
  PanelFooter,
  Button,
  Badge,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Textarea,
  Select,
  SelectOption,
  SectionHeader,
} from '../../components/ui'
import { AIConsoleShell, type AIConsoleShellSummaryItem } from '../../components/ai-console/AIConsoleShell'
import {
  useAppletMappings,
  usePromptVersions,
  useSandboxRun,
  useSandboxDrafts,
  useSaveSandboxDraft,
  useDeleteSandboxDraft,
} from '../../hooks/useAIConsole'
import type { SandboxDraft, SandboxRunResponse } from './types'
import { APPLETS } from './constants'
import { resolveSelectedMapping } from './agentLensUtils'

const DEFAULT_TEST_INPUT = '{\n  "query": "Summarize the latest signal and cite the evidence."\n}'

const AgentTestingPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectedAgent = searchParams.get('agent') ?? undefined
  const preselectedApplet = searchParams.get('applet') ?? undefined

  const [selectedApplet, setSelectedApplet] = useState<string>(preselectedApplet || 'chat')
  const [selectedAgent, setSelectedAgent] = useState<string>(preselectedAgent || '')
  const [activeTab, setActiveTab] = useState<'sandbox' | 'drafts'>('sandbox')

  const [promptText, setPromptText] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [modelId, setModelId] = useState('gpt-4o')
  const [testInput, setTestInput] = useState(DEFAULT_TEST_INPUT)
  const [temperature, setTemperature] = useState(0.7)
  const [recentRuns, setRecentRuns] = useState<SandboxRunResponse[]>([])

  const { data: mappings } = useAppletMappings(selectedApplet)
  const { mutate: runSandbox, isPending: running } = useSandboxRun()
  const { data: drafts, isLoading: draftsLoading } = useSandboxDrafts(selectedAgent || undefined)
  const { mutate: saveDraft, isPending: savingDraft } = useSaveSandboxDraft()
  const { mutate: deleteDraft } = useDeleteSandboxDraft()

  const agents = useMemo(() => {
    if (!mappings) return []
    const unique = new Map<string, string>()
    for (const mapping of mappings) {
      if (!unique.has(mapping.agent_identifier)) {
        unique.set(mapping.agent_identifier, mapping.display_name || mapping.agent_identifier)
      }
    }
    return Array.from(unique.entries())
  }, [mappings])

  const selectedMapping = useMemo(
    () => resolveSelectedMapping(mappings ?? [], { agentIdentifier: selectedAgent }),
    [mappings, selectedAgent],
  )
  const { data: promptVersions } = usePromptVersions(selectedMapping?.langfuse_prompt_key)
  const linkedPrompt = promptVersions?.[0]

  useEffect(() => {
    if (!agents.length) {
      setSelectedAgent('')
      return
    }
    if (selectedAgent && agents.some(([id]) => id === selectedAgent)) {
      return
    }
    setSelectedAgent(agents[0][0])
  }, [agents, selectedAgent])

  const appletLabel = APPLETS.find((applet) => applet.id === selectedApplet)?.label ?? selectedApplet
  const shellSummary: AIConsoleShellSummaryItem[] = [
    {
      label: 'Applet',
      value: appletLabel,
      detail: 'All experiments on this page stay scoped to one product area at a time.',
    },
    {
      label: 'Selected agent',
      value: selectedAgent || 'Choose one',
      detail: selectedAgent
        ? 'This is the mapped producer you are testing right now.'
        : 'Pick a mapped agent before you run the scratchpad.',
      tone: selectedAgent ? ('brand' as const) : ('warning' as const),
    },
    {
      label: 'Managed prompt',
      value: selectedMapping?.langfuse_prompt_key || 'No linked prompt',
      detail: selectedMapping?.langfuse_prompt_key
        ? 'The live prompt is shown below so you know what production is currently using.'
        : 'You can still test free-form changes, but there is no managed prompt history attached yet.',
      tone: selectedMapping?.langfuse_prompt_key ? ('success' as const) : ('warning' as const),
    },
    {
      label: 'Recent scratchpad runs',
      value: recentRuns.length,
      detail: 'These runs are fast experiments. Use formal evaluations before you treat a change as proven.',
    },
  ]

  const handleRun = () => {
    let parsedInput: Record<string, unknown> = {}
    try {
      parsedInput = JSON.parse(testInput)
    } catch {
      parsedInput = { raw: testInput }
    }

    runSandbox(
      {
        applet_id: selectedApplet,
        page_id: selectedMapping?.page_id,
        data_element_key: selectedMapping?.data_element_key,
        agent_identifier: selectedAgent,
        flow_identifier: selectedMapping?.flow_identifier,
        prompt_text: promptText || undefined,
        system_prompt: systemPrompt || undefined,
        model_id: modelId,
        config_overrides: { temperature },
        test_input: parsedInput,
      },
      {
        onSuccess: (result) => {
          setRecentRuns((previous) => [result, ...previous].slice(0, 12))
          setActiveTab('sandbox')
        },
      },
    )
  }

  const handleSaveDraft = () => {
    if (!selectedAgent) return
    saveDraft(
      {
        applet_id: selectedApplet,
        agent_identifier: selectedAgent,
        flow_identifier: selectedMapping?.flow_identifier,
        title: `${appletLabel} scratchpad — ${selectedAgent}`,
        prompt_text: promptText || undefined,
        system_prompt: systemPrompt || undefined,
        model_id: modelId,
        config_overrides: { temperature },
      },
      {
        onSuccess: () => setActiveTab('drafts'),
      },
    )
  }

  const loadDraft = (draft: SandboxDraft) => {
    setSelectedApplet(draft.applet_id)
    setSelectedAgent(draft.agent_identifier)
    setPromptText(draft.prompt_text || '')
    setSystemPrompt(draft.system_prompt || '')
    setModelId(draft.model_id || 'gpt-4o')
    if (draft.config_overrides && typeof draft.config_overrides.temperature === 'number') {
      setTemperature(draft.config_overrides.temperature)
    } else {
      setTemperature(0.7)
    }
    setActiveTab('sandbox')
  }

  return (
    <Layout>
      <Page>
        <PageBody>
          <AIConsoleShell
            sectionId="testing"
            title="Try a change without confusing experiment with proof."
            description="Testing Hub is the safe scratchpad for prompt and model experiments. It keeps quick tests separate from formal evaluations so non-technical reviewers can tell what is exploratory versus what is validated."
            plainLanguageSummary="Use this page to answer, 'What happens if we change the prompt, model, or instructions?' Run a quick test here, compare the result, then move the winning idea into formal evaluations before you ship it."
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => navigate('/evals')}>
                  Open formal evaluations
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/ai-console/agent-lens?applet=${selectedApplet}${selectedAgent ? `&agent=${selectedAgent}` : ''}${selectedMapping?.data_element_key ? `&element=${selectedMapping.data_element_key}` : ''}`,
                    )
                  }
                >
                  Inspect runs instead
                </Button>
              </div>
            }
            summaryItems={shellSummary}
          />

          <div className="mt-6 grid grid-cols-1 xl:grid-cols-[1.2fr,1fr] gap-6">
            <Panel>
              <PanelHeader>
                <PanelTitle>How This Workspace Works</PanelTitle>
                <PanelDescription>
                  Keep experiments fast here, then switch to the right place when you need evidence or root-cause detail.
                </PanelDescription>
              </PanelHeader>
              <PanelBody>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ModeCard
                    title="Formal Evals"
                    badge="Authoritative"
                    description="Use the dedicated evaluation workspace when you need repeatable datasets, stored run history, and measurable pass/fail evidence."
                    actionLabel="Open Evals"
                    onAction={() => navigate('/evals')}
                  />
                  <ModeCard
                    title="Agent Lens"
                    badge="Investigate"
                    description="Jump into traces, node detail, and feedback signals for the currently selected applet and agent."
                    actionLabel="Open Lens"
                    onAction={() =>
                      navigate(
                        `/ai-console/agent-lens?applet=${selectedApplet}${selectedAgent ? `&agent=${selectedAgent}` : ''}${selectedMapping?.data_element_key ? `&element=${selectedMapping.data_element_key}` : ''}`,
                      )
                    }
                  />
                  <ModeCard
                    title="Scratchpad"
                    badge="Fast"
                    description="Use the workbench below for prompt overrides, model comparisons, and quick hypothesis checks before formalizing changes."
                    actionLabel="Stay Here"
                    onAction={() => setActiveTab('sandbox')}
                    emphasized
                  />
                </div>
              </PanelBody>
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Current Experiment Target</PanelTitle>
                <PanelDescription>
                  Narrow the scratchpad to one mapped producer before you run an experiment.
                </PanelDescription>
              </PanelHeader>
              <PanelBody>
                <div className="space-y-4">
                  <Select value={selectedApplet} onValueChange={setSelectedApplet}>
                    {APPLETS.map((applet) => (
                      <SelectOption key={applet.id} value={applet.id}>
                        {applet.label}
                      </SelectOption>
                    ))}
                  </Select>

                  <Select
                    value={selectedAgent}
                    onValueChange={setSelectedAgent}
                    placeholder={agents.length ? 'Select agent' : 'No mapped agents'}
                  >
                    {agents.map(([id, label]) => (
                      <SelectOption key={id} value={id}>
                        {label}
                      </SelectOption>
                    ))}
                  </Select>

                  <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2/60">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                      Current Scope
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <ScopeLine label="Page" value={selectedMapping?.page_id || 'Unmapped'} />
                      <ScopeLine
                        label="Element"
                        value={selectedMapping?.data_element_key || 'Choose an agent'}
                      />
                      <ScopeLine label="Flow" value={selectedMapping?.flow_identifier || 'Unknown'} />
                      <ScopeLine
                        label="Prompt"
                        value={selectedMapping?.langfuse_prompt_key || 'No linked prompt'}
                      />
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/ai-console/agent-lens?applet=${selectedApplet}${selectedAgent ? `&agent=${selectedAgent}` : ''}${selectedMapping?.data_element_key ? `&element=${selectedMapping.data_element_key}` : ''}`,
                          )
                        }
                      >
                        Open Lens
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate('/evals')}>
                        Open Evals
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-800/40 dark:bg-amber-950/10">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                      Scratchpad runs are intentionally ephemeral
                    </p>
                    <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                      These results help you iterate quickly, but they are not formal evaluation evidence. Promote a promising change into the Evals workspace before you treat it as proven.
                    </p>
                  </div>
                </div>
              </PanelBody>
            </Panel>
          </div>

          <SectionHeader
            className="mt-8"
            title="Try a Change Safely"
            description="Override prompts, swap models, and run a focused experiment against the selected agent without touching the live applet."
          />

          <div className="grid grid-cols-1 xl:grid-cols-[1.1fr,0.9fr] gap-6">
            <Panel>
              <PanelHeader>
                <PanelTitle>Experiment Setup</PanelTitle>
                <PanelDescription>
                  Keep the brief tight. Small, deliberate changes are easier to explain and verify than large rewrites.
                </PanelDescription>
              </PanelHeader>
              <PanelBody>
                <div className="space-y-4">
                  {linkedPrompt && (
                    <div className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-sm font-medium text-charcoal dark:text-gray-100">
                          Linked Prompt
                        </span>
                        <Badge variant="outline">v{linkedPrompt.version}</Badge>
                      </div>
                      <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-xs font-mono text-charcoal dark:bg-dark-surface-2 dark:text-gray-100">
                        {linkedPrompt.prompt || 'No prompt text'}
                      </pre>
                    </div>
                  )}

                  <Field label="Model">
                    <Select value={modelId} onValueChange={setModelId}>
                      <SelectOption value="gpt-4o">gpt-4o</SelectOption>
                      <SelectOption value="gpt-4o-mini">gpt-4o-mini</SelectOption>
                      <SelectOption value="gpt-4.1">gpt-4.1</SelectOption>
                      <SelectOption value="claude-sonnet-4-20250514">Claude Sonnet 4</SelectOption>
                      <SelectOption value="claude-3-5-haiku-20241022">Claude Haiku 3.5</SelectOption>
                    </Select>
                  </Field>

                  <Field label={`Temperature: ${temperature.toFixed(1)}`}>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={temperature}
                      onChange={(event) => setTemperature(parseFloat(event.target.value))}
                      className="w-full accent-eliza-red"
                    />
                  </Field>

                  <Field label="System Prompt Override">
                    <Textarea
                      value={systemPrompt}
                      onChange={(event) => setSystemPrompt(event.target.value)}
                      className="min-h-[110px] text-sm"
                      placeholder="Use this only when you need to test a deliberate system-level change."
                    />
                  </Field>

                  <Field label="Prompt / Role Override">
                    <Textarea
                      value={promptText}
                      onChange={(event) => setPromptText(event.target.value)}
                      className="min-h-[160px] font-mono text-sm"
                      placeholder="Capture the exact behavior adjustment you want to test."
                    />
                  </Field>

                  <Field label="Test Input">
                    <Textarea
                      value={testInput}
                      onChange={(event) => setTestInput(event.target.value)}
                      className="min-h-[160px] font-mono text-sm"
                    />
                  </Field>
                </div>
              </PanelBody>
              <PanelFooter>
                <div className="flex items-center gap-2">
                  <Button onClick={handleRun} disabled={!selectedAgent || running}>
                    {running ? 'Running…' : 'Run experiment'}
                  </Button>
                  <Button variant="outline" onClick={handleSaveDraft} disabled={!selectedAgent || savingDraft}>
                    {savingDraft ? 'Saving…' : 'Save draft'}
                  </Button>
                </div>
              </PanelFooter>
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle>Scratchpad Results and Saved Setups</PanelTitle>
                <PanelDescription>
                  Review fresh runs in the scratchpad, then save anything worth revisiting.
                </PanelDescription>
              </PanelHeader>
              <PanelBody>
                <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
                  <TabsList>
                    <TabsTrigger value="sandbox">Recent Runs</TabsTrigger>
                    <TabsTrigger value="drafts">Saved Drafts</TabsTrigger>
                  </TabsList>

                  <TabsContent value="sandbox">
                    {recentRuns.length === 0 ? (
                      <EmptyState
                        title="No sandbox runs yet"
                        description="Run an experiment and the latest output will appear here with model, latency, and cost context."
                      />
                    ) : (
                      <div className="space-y-3">
                        {recentRuns.map((run, index) => (
                          <Panel key={`${run.run_id}-${index}`} className="border border-gray-200 dark:border-dark-border/50">
                            <PanelHeader>
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <PanelTitle className="text-sm">{run.agent_identifier}</PanelTitle>
                                  <PanelDescription>Scratchpad experiment #{recentRuns.length - index}</PanelDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant={run.status === 'completed' ? 'success' : 'danger'}>
                                    {run.status}
                                  </Badge>
                                  <Badge variant="outline">{run.model_id || 'default model'}</Badge>
                                </div>
                              </div>
                            </PanelHeader>
                            <PanelBody>
                              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-3">
                                <span>{run.latency_ms.toFixed(0)}ms</span>
                                {run.cost_usd != null && <span>${run.cost_usd.toFixed(4)}</span>}
                              </div>
                              <pre className="max-h-[280px] overflow-auto rounded-xl bg-gray-50 p-4 text-xs font-mono text-charcoal dark:bg-dark-surface-2 dark:text-gray-100 whitespace-pre-wrap">
                                {typeof run.output === 'string' ? run.output : JSON.stringify(run.output, null, 2)}
                              </pre>
                            </PanelBody>
                          </Panel>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="drafts">
                    {draftsLoading ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, index) => (
                          <Skeleton key={index} variant="rounded" height={88} />
                        ))}
                      </div>
                    ) : drafts && drafts.length > 0 ? (
                      <div className="space-y-3">
                        {drafts.map((draft) => (
                          <Panel key={draft.id} className="border border-gray-200 dark:border-dark-border/50">
                            <PanelBody>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-charcoal dark:text-gray-100">
                                    {draft.title || `Draft #${draft.id}`}
                                  </p>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {draft.agent_identifier}
                                    {draft.model_id ? ` · ${draft.model_id}` : ''}
                                    {draft.updated_at ? ` · ${new Date(draft.updated_at).toLocaleDateString()}` : ''}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button size="sm" onClick={() => loadDraft(draft)}>
                                    Load
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => deleteDraft(draft.id)}>
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </PanelBody>
                          </Panel>
                        ))}
                      </div>
                    ) : (
                      <EmptyState
                        title="No saved drafts"
                        description="Save a scratchpad configuration when you have a prompt variation worth coming back to."
                      />
                    )}
                  </TabsContent>
                </Tabs>
              </PanelBody>
            </Panel>
          </div>
        </PageBody>
      </Page>
    </Layout>
  )
}

const ModeCard: React.FC<{
  title: string
  badge: string
  description: string
  actionLabel: string
  onAction: () => void
  emphasized?: boolean
}> = ({ title, badge, description, actionLabel, onAction, emphasized = false }) => (
  <div
    className={`rounded-2xl border p-4 ${
      emphasized
        ? 'border-eliza-red/25 bg-eliza-red/5 dark:border-eliza-red/40 dark:bg-eliza-red/10'
        : 'border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface'
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-sm font-medium text-charcoal dark:text-gray-100">{title}</h3>
      <Badge variant={emphasized ? 'default' : 'outline'}>{badge}</Badge>
    </div>
    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{description}</p>
    <Button variant={emphasized ? 'default' : 'outline'} size="sm" className="mt-4" onClick={onAction}>
      {actionLabel}
    </Button>
  </div>
)

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-charcoal dark:text-gray-100 mb-1.5">
      {label}
    </label>
    {children}
  </div>
)

const EmptyState: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="rounded-2xl border border-dashed border-gray-200 p-8 text-center dark:border-dark-border/60">
    <p className="text-sm font-medium text-charcoal dark:text-gray-100">{title}</p>
    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{description}</p>
  </div>
)

const ScopeLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <p className="mt-1 break-words text-sm font-medium text-charcoal dark:text-gray-100">
      {value}
    </p>
  </div>
)

export default AgentTestingPage
